import { Fragment, useState } from 'react';

import { useTranslation } from 'react-i18next';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Alert,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, LoadingState, TablePageShell } from '@/shared/ui';

import { usePromotionAudit, usePromotions } from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { PROMOTION_STATUS_COLOR } from '../../model/statuses';

/**
 * "Audit" — promo audit log (plan-011 §8.11).
 *
 * Contract-driven adaptation: the shipped BFF only exposes `GET
 * /promotions/{promotionId}/audit` — a per-promotion lifecycle+edit trail. There is no tenant-wide
 * audit feed across campaigns/coupons/budgets/policies as the design spec's cross-entity picker
 * envisioned, so this screen requires picking one promotion first rather than showing a global
 * feed with an "Object type" filter.
 */
export function PromoAuditPanel() {
  const { t } = useTranslation('promotions');
  const [promotionId, setPromotionId] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const promotions = usePromotions({ size: 200 });
  const audit = usePromotionAudit(promotionId || undefined, { page, size }, { enabled: Boolean(promotionId) });

  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('audit.scopeNotice')}</Alert>

      <TextField
        select
        size="small"
        label={t('audit.promotionSelectLabel')}
        value={promotionId}
        onChange={(e) => {
          setPromotionId(e.target.value);
          setPage(0);
        }}
        sx={{ maxWidth: 360 }}
      >
        <MenuItem value="">{t('audit.pickPromotion')}</MenuItem>
        {(promotions.data?.content ?? []).map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </TextField>

      {!promotionId ? (
        <EmptyState icon={<HistoryRoundedIcon sx={{ fontSize: 48 }} />} title={t('audit.noSelection.title')} description={t('audit.noSelection.description')} />
      ) : audit.isLoading ? (
        <LoadingState label={t('audit.loading')} />
      ) : (audit.data?.content.length ?? 0) === 0 ? (
        <EmptyState title={t('audit.empty.title')} />
      ) : (
        <TablePageShell
          footer={
            <CompactTablePagination
              count={audit.data?.totalElements ?? 0}
              page={page}
              rowsPerPage={size}
              hideRowsPerPage
              onPageChange={(_e, next) => setPage(next)}
              onRowsPerPageChange={(e) => {
                setSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
            />
          }
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('audit.table.date')}</TableCell>
                  <TableCell>{t('audit.table.author')}</TableCell>
                  <TableCell>{t('audit.table.event')}</TableCell>
                  <TableCell>{t('audit.table.reason')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(audit.data?.content ?? []).map((entry) => (
                  <Fragment key={entry.id}>
                    <TableRow hover>
                      <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                      <TableCell>{entry.actor ?? '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {entry.fromStatus ? (
                            <Chip size="small" variant="outlined" color={PROMOTION_STATUS_COLOR[entry.fromStatus]} label={entry.fromStatus} />
                          ) : null}
                          <span>→</span>
                          <Chip size="small" color={PROMOTION_STATUS_COLOR[entry.toStatus]} label={entry.toStatus} />
                        </Stack>
                      </TableCell>
                      <TableCell>{entry.reason ?? '—'}</TableCell>
                      <TableCell align="right">
                        {entry.diff ? (
                          <IconButton
                            size="small"
                            aria-label={t('audit.expandRow')}
                            aria-expanded={expandedId === entry.id}
                            onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                            sx={{ transform: expandedId === entry.id ? 'rotate(180deg)' : undefined }}
                          >
                            <ExpandMoreRoundedIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {entry.diff ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedId === entry.id ? undefined : 'none' }}>
                          <Collapse in={expandedId === entry.id}>
                            <Stack spacing={0.5} sx={{ py: 1.5, pl: 2 }}>
                              {Object.entries(entry.diff).map(([key, value]) => (
                                <Typography key={key} variant="body2">
                                  <strong>{key}:</strong> {JSON.stringify(value)}
                                </Typography>
                              ))}
                            </Stack>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}
    </Stack>
  );
}
